import { useBunja } from 'bunja/react';
import cx from 'classnames';
import { useAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { Link } from '@flipster/ui';
import DocumentSearchIcon from '@flipster/ui/assets/svgs/document-search.svg';
import { getFlipsterConfigsV1Key, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import segment from '@flipster/utils/segment';
import { PositionEvent } from '@flipster/utils/segment/events/position';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';
import TimeTriggerTpslModal from '@web/features/modals/TimeTriggerTpslModal';

import PositionSectionHeader from './position-section-header';
import { PositionTable } from './position-table';
import styles from './positionSection.module.scss';
import { yourPositionsBunja } from './state';

const loggingMap = {
  open: 'Open' as const,
  pending: 'Pending' as const,
};

const YourPositions = () => {
  const transCommon = useTranslations('Common.r250401');
  const transTrade = useTranslations('Trade.r250401');
  const { symbol } = useBunja(tradeSymbolBunja);
  const { showAllSymbolAtom, positionTabTypeAtom } = useBunja(yourPositionsBunja);
  const [tab, setTab] = useAtom(positionTabTypeAtom);
  const [showAllSymbol, setShowAllSymbol] = useAtom(showAllSymbolAtom);

  const { isLoggedIn, user } = useAuth();

  const onChangeTab = (selectedTab: 'open' | 'pending') => {
    setTab(selectedTab);
    segment.track(PositionEvent.PositionOrderStatusTabClick, {
      tab_type: 'Your positions',
      execution_stage: loggingMap[selectedTab],
    });
  };

  const onClickSignIn = () => {
    segment.track(PositionEvent.PositionTradeSignInClick, { symbol });
  };

  const toggleShowAllSymbol = () => {
    setShowAllSymbol((prev) => {
      if (user) setLocalStorage(getFlipsterConfigsV1Key(user.userId, 'showAllPerp'), !prev);
      return !prev;
    });
  };

  useEffect(() => {
    if (!user) return;
    const showAllPerp = (getLocalStorage(getFlipsterConfigsV1Key(user.userId, 'showAllPerp')) as boolean) ?? true;
    setShowAllSymbol(showAllPerp);
  }, [user, setShowAllSymbol]);

  return (
    <>
      <PositionSectionHeader
        tab={tab}
        onChangeTab={onChangeTab}
        showAllSymbol={showAllSymbol}
        toggleShowAllSymbol={toggleShowAllSymbol}
      />
      {isLoggedIn ? (
        <div className={styles.wrapper}>
          <PositionTable type={tab} className={styles.positions} />
        </div>
      ) : (
        <div className={cx(styles.wrapper, styles.signIn)}>
          <DocumentSearchIcon />
          <p>{transTrade('Login_required_to_view_positions')}</p>
          <Link href='/login' onClick={onClickSignIn} size='small' shape='secondary'>
            {transCommon('Log_in')}
          </Link>
        </div>
      )}
      {/* Time-trigger sunset: modal kept only for leftover time-trigger TP/SL. */}
      <TimeTriggerTpslModal />
    </>
  );
};

export default YourPositions;
